import React, { memo, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Fade from '@material-ui/core/Fade';
import Radio from '@material-ui/core/Radio';
import Checkbox from '@material-ui/core/Checkbox';
import Box from '@material-ui/core/Box';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grow from '@material-ui/core/Grow';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import CancelIcon from '@material-ui/icons/Cancel';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import some from 'lodash/some';
import take from 'lodash/take';
import toString from 'lodash/toString';
import classNames from 'classnames';
import messages from 'aurora-translations';
import Popper from '@material-ui/core/Popper';
import ADGMImage from '../ADGMImage';
import { DROPDOWN_HEIGHT, PER_PAGE } from './constants';
import styles from './styles';

const ADGMSelect = props => {
  const {
    classes,
    label,
    name,
    value,
    options,
    required,
    onChange,
    placeholder,
    customClasses,
    type,
    multi,
    search,
    style,
    disabled,
    // selectStyle,
    tabIndex,
    error,
    intl,
    hideClearSelectionBtn,
    infoHint,
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inFocus, setInFocus] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedOptions, setPaginatedOptions] = useState([]);
  const scrollBarRef = useRef();

  useEffect(() => {
    if (isString(value) || isNumber(value)) {
      const valueObj = options?.find(opt => toString(opt.value) === toString(value));
      setSelectedValue(valueObj || null);
      return;
    }
    if (isArray(value) && multi && !some(value, isObject)) {
      const valueArr = value.map(val => {
        return options?.find(opt => toString(opt.value) === toString(val));
      });
      setSelectedValue(valueArr || null);
      return;
    }
    setSelectedValue(value);
  }, [value, options]);

  useEffect(() => {
    if (search) {
      setPaginatedOptions(
        take(
          options?.filter(opt => opt.label?.toLocaleLowerCase().includes(searchValue?.toLocaleLowerCase?.())),
          PER_PAGE * currentPage,
        ),
      );
    }
  }, [currentPage, options, searchValue]);

  const isScrollBarVisible = useMemo(() => scrollBarRef.current?.clientHeight === DROPDOWN_HEIGHT, [showDropdown]);

  const fetchNext = useCallback(() => {
    setCurrentPage(page => page + 1);
  }, [currentPage]);

  const onFocus = useCallback(() => setInFocus(true), []);

  const onBlur = useCallback(() => setInFocus(false), []);

  const handleClickAway = useCallback(() => {
    setShowDropdown(false);
    setInFocus(false);
  }, []);

  const handleSelect = event => {
    setAnchorEl(event.currentTarget);
    setShowDropdown(!showDropdown);
    setInFocus(!inFocus);
  };

  const handleCancelSelect = useCallback(
    event => {
      event.stopPropagation();
      setSelectedValue(null);
      onChange && onChange(name, { value: null } || null);
    },
    [name, onChange],
  );

  const handleItemSelect = useCallback(
    item => () => {
      const newItem = multi
        ? selectedValue
          ? selectedValue.indexOf(item) > -1
            ? [...selectedValue.filter(selected => selected.value !== item.value)]
            : [...selectedValue, item]
          : [item]
        : item;
      setSelectedValue(newItem);
      onChange && onChange(name, newItem);
    },
    [multi, name, onChange, selectedValue],
  );

  const handleRadioSelect = useCallback(
    e => {
      setSelectedValue(options?.find(opt => opt.value === +e.target.value));
      setShowDropdown(false);
      onChange &&
        onChange(
          e.target.name,
          options?.find(opt => opt.value === +e.target.value),
        );
    },
    [onChange, options],
  );

  const iconRender = useCallback(() => {
    if (hideClearSelectionBtn === false && !isEmpty(selectedValue)) {
      return (
        <Fade in={!isEmpty(selectedValue)}>
          <IconButton
            aria-label='cancel'
            size='small'
            tabIndex={-1}
            onClick={!disabled ? handleCancelSelect : undefined}
            className={classNames({ [classes.disabledSelect]: disabled })}
          >
            <CancelIcon fontSize='inherit' />
          </IconButton>
        </Fade>
      );
    }

    return showDropdown ? (
      <Fade in={showDropdown}>
        <ExpandLess />
      </Fade>
    ) : (
      <Fade in={!showDropdown}>
        <ExpandMore />
      </Fade>
    );
  }, [disabled, handleCancelSelect, selectedValue, showDropdown, classes.disabledSelect]);

  const itemClass = useCallback(
    opt => {
      return classNames(classes.listItem, {
        [classes.selectedItem]:
          type === 'select' && multi
            ? (selectedValue || []).find(item => item.value === opt.value)
            : selectedValue?.value && selectedValue.value === opt.value,
        [classes.selectedButton]:
          type === 'button' && multi
            ? (selectedValue || []).find(item => item.value === opt.value)
            : selectedValue?.value && selectedValue.value === opt.value,
      });
    },
    [type, multi, selectedValue],
  );

  const renderList = arr => {
    if (!isArray(arr)) return null;

    return (
      <div
        ref={scrollBarRef}
        className={classNames(classes.listContainer, {
          [classes.listContainerMulti]: multi,
          [classes.listContainerMargin]: isScrollBarVisible,
          [classes.buttonsContainer]: type === 'button',
        })}
      >
        {type === 'radio' ? (
          <div className={classes.radioContainer}>
            <RadioGroup
              aria-label={name}
              name={name}
              value={selectedValue?.value || ''}
              onChange={handleRadioSelect}
              className={classes.radio}
            >
              {(arr || []).map(opt => (
                <FormControlLabel
                  disabled={disabled}
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
          </div>
        ) : (
          <ul>
            {(arr || []).map(opt => (
              <li key={opt.value}>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={!disabled ? handleItemSelect(opt) : undefined}
                  onKeyPress={handleItemSelect(opt)}
                  className={itemClass(opt)}
                >
                  <Typography component='p' disabled={disabled}>
                    {opt.label}
                  </Typography>
                  {!multi && selectedValue?.value === opt?.value && (
                    <ADGMImage name='check_icon_green.svg' alt='checked' />
                  )}
                  {multi && (
                    <Checkbox
                      edge='start'
                      checked={!!(selectedValue || [])?.find(item => item.value === opt.value)}
                      tabIndex={-1}
                      disableRipple
                      classes={{
                        root: classes.itemCheckbox,
                        checked: classes.itemCheckboxChecked,
                      }}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const displayText = () => {
    if (isArray(selectedValue) && !isEmpty(selectedValue)) {
      const text = [];
      selectedValue.forEach(item => {
        text.push(item.label);
      });
      return text.join(',');
    }
    return get(selectedValue, 'label', placeholder);
  };

  return (
    <div className={classNames(classes.container, customClasses?.container)} style={style}>
      {label && (
        <Typography
          component='p'
          className={classNames(classes.label, required && classes.requiredRed, customClasses?.label)}
        >
          {label} {required && <span>*</span>}
        </Typography>
      )}
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          role='button'
          onClick={!disabled ? handleSelect : undefined}
          onKeyPress={!disabled ? handleSelect : undefined}
          onFocus={!disabled ? onFocus : undefined}
          onBlur={!disabled ? onBlur : undefined}
          tabIndex={-1}
        >
          <div
            className={classNames(
              classes.select,
              {
                [classes.disabledSelect]: disabled,
                [classes.errorSelect]: error,
                [classes.inFocus]: inFocus || showDropdown,
                [classes.selected]: !isEmpty(selectedValue),
                [customClasses?.selected]: !isEmpty(selectedValue),
              },
              customClasses?.select,
            )}
            role='listbox'
            tabIndex={tabIndex || 0}
          >
            <Typography component='p'>{displayText()}</Typography>
            {iconRender()}
          </div>
          <Grow in={!isEmpty(error)} timeout={400}>
            <span className={classes.errorMsg}>{error}</span>
          </Grow>
          <Popper
            open={showDropdown}
            anchorEl={anchorEl}
            placement='bottom-start'
            disablePortal
            style={{ width: anchorEl?.clientWidth, zIndex: 999 }}
            modifiers={{
              flip: {
                enabled: true,
              },
              preventOverflow: {
                enabled: true,
                boundariesElement: 'scrollParent',
              },
            }}
          >
            {/* <Paper
              className={classNames(selectStyle && selectStyle, classes.dropdownContainer, customClasses?.dropdown, {
                [classes.dropdownContainerError]: error,
              })}
            > */}
            {search ? (
              <div className={classes.searchContainer}>
                <ADGMImage name='Search.svg' alt='search' />
                <input
                  type='text'
                  id={`search-${name}`}
                  name={`search-${name}`}
                  placeholder={intl.formatMessage(messages.search_text)}
                  autoComplete='off'
                  value={searchValue}
                  onClick={e => e.stopPropagation()}
                  onKeyPress={e => e.stopPropagation()}
                  onChange={e => setSearchValue(e.target.value)}
                  className={classes.searchInput}
                />
                <InfiniteScroll
                  dataLength={paginatedOptions?.length}
                  next={fetchNext}
                  hasMore
                  scrollableTarget={`scrollableDiv-${name}`}
                >
                  <div
                    id={`scrollableDiv-${name}`}
                    ref={scrollBarRef}
                    className={classNames(classes.listContainer, {
                      [classes.listContainerMulti]: multi,
                      [classes.listContainerMargin]: isScrollBarVisible,
                    })}
                  >
                    <ul>
                      {(paginatedOptions || []).map((opt, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={`${opt?.label || ''}${opt?.value || ''}${i}`}>
                          <div
                            role='button'
                            tabIndex={0}
                            onClick={!disabled ? handleItemSelect(opt) : undefined}
                            onKeyPress={handleItemSelect(opt)}
                            className={itemClass(opt)}
                          >
                            <Typography component='p' disabled={disabled}>
                              {opt.label}
                            </Typography>
                            {!multi && selectedValue?.value === opt?.value && (
                              <ADGMImage name='check_icon_green.svg' alt='checked' />
                            )}
                            {multi && (
                              <Checkbox
                                edge='start'
                                checked={!!(selectedValue || [])?.find(item => item.value === opt.value)}
                                tabIndex={-1}
                                disableRipple
                                classes={{
                                  root: classes.itemCheckbox,
                                  checked: classes.itemCheckboxChecked,
                                }}
                              />
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </InfiniteScroll>
              </div>
            ) : (
              renderList(options)
            )}
            {/* </Paper> */}
          </Popper>
        </div>
      </ClickAwayListener>
      {infoHint && <Box className={classes.hintText}>{infoHint}</Box>}
    </div>
  );
};

ADGMSelect.propTypes = {
  classes: PropTypes.object.isRequired,
  style: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  name: PropTypes.string,
  value: PropTypes.any,
  required: PropTypes.bool,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.string,
  multi: PropTypes.bool,
  search: PropTypes.bool,
  tabIndex: PropTypes.number,
  disabled: PropTypes.bool,
  customClasses: PropTypes.object,
  error: PropTypes.string,
  // selectStyle: PropTypes.object,
  intl: PropTypes.object.isRequired,
  hideClearSelectionBtn: PropTypes.bool,
  infoHint: PropTypes.string,
};

ADGMSelect.defaultProps = {
  style: {},
  label: null,
  name: null,
  value: null,
  customClasses: null,
  type: 'select',
  placeholder: <FormattedMessage {...messages.select} />,
  required: false,
  multi: false,
  search: false,
  tabIndex: 0,
  disabled: false,
  error: null,
  selectStyle: {},
  hideClearSelectionBtn: false,
  infoHint: '',
};

export default withStyles(styles)(memo(injectIntl(ADGMSelect)));
